/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * isValidEmailFormat function.
 *
 * @param {*} email - Parameter description
 *
 * @returns {*} Return value description
 */
export const isValidEmailFormat = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validates a Brazilian CPF including its two check digits (ignores formatting
 * punctuation). Rejects wrong-length inputs and repeated-digit sequences.
 *
 * @param cpf CPF string, with or without dots/hyphen.
 * @returns `true` when the CPF is structurally valid.
 */
export const isValidCPFFormat = (cpf: string): boolean => {
    cpf = cpf.replace(/[^\d]+/g, '');

    if (cpf.length !== 11) return false;

    if (/^(\d)\1{10}$/.test(cpf)) return false;

    const calcDigit = (base: string, factor: number): number => {
      const sum = base
        .split('')
        .reduce((acc, num, i) => acc + parseInt(num) * (factor - i), 0);
      const remainder = sum % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };

    const firstDigit = calcDigit(cpf.slice(0, 9), 10);
    const secondDigit = calcDigit(cpf.slice(0, 10), 11);

    return cpf[9] === String(firstDigit) && cpf[10] === String(secondDigit);
}

/**
 * isValidCNPJFormat function.
 *
 * @param {*} cnpj - Parameter description
 *
 * @returns {*} Return value description
 */
/**
 * The 14 CNPJ positions: 12 alphanumeric, then 2 numeric check digits.
 *
 * Alphanumeric since Receita Federal's July 2026 rollout (NT Conjunta
 * 2025.001) -- the first 8 positions are the raiz and the next 4 the
 * ordem, and both accept A-Z. The check digits stayed numeric.
 */
const CNPJ_SHAPE = /^[0-9A-Z]{12}[0-9]{2}$/;

/**
 * A CNPJ character's numeric weight: its ASCII code minus 48.
 *
 * Digits therefore keep their own value and letters continue past 9
 * (A=17, B=18, ... Z=42). This is what makes the alphanumeric format
 * backward compatible: an all-numeric CNPJ yields exactly the check
 * digits it always had, so existing records keep validating untouched.
 */
const cnpjCharWeight = (character: string): number => character.charCodeAt(0) - 48;

/**
 * Is valid c n p j format.
 */
export const isValidCNPJFormat = (cnpj: string) => {
    const cleaned = cnpj.replace(/[^0-9A-Za-z]+/g, '').toUpperCase();

    if (!CNPJ_SHAPE.test(cleaned)) return false;

    // Generalized from the old digit-only guard: a CNPJ that is one
    // character repeated 14 times can satisfy mod 11 by accident.
    if (/^(.)\1{13}$/.test(cleaned)) return false;

    const calcularDigito = (base: string, pesos: number[]): number => {
      const soma = base
        .split('')
        .reduce((acc, character, i) => acc + cnpjCharWeight(character) * pesos[i], 0);
      const resto = soma % 11;
      return resto < 2 ? 0 : 11 - resto;
    };

    const base = cleaned.slice(0, 12);
    const primeiroDigito = calcularDigito(base, [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);
    const segundoDigito = calcularDigito(base + primeiroDigito, [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]);

    const digitosVerificadores = cleaned.slice(12);

    return digitosVerificadores === `${primeiroDigito}${segundoDigito}`;
  }